import './assets/fonts/fonts.css';
import './App.css';
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { PlanFeaturesProvider } from './contexts/PlanFeaturesContext';
import { TranslationProvider } from './contexts/TranslationContext';
import { LocationProvider } from './contexts/LocationContext';
import { SearchStateProvider } from './contexts/SearchStateContext';
import Search from './pages/search/search';
import Results from './pages/results/results';
import AuthCallback from './pages/authCallback/authCallback';
import Header from './components/header/header';
import Footer from './components/footer/footer';
import config from './config/appConfig';

// Import location service for background detection
import locationService from './services/locationService';
import { getToken } from './services/apiTokenService';
import { isLocalDevMode } from './utils/devMode';

// Shared "no plan code available, go to the plan picker" navigation used by
// FindCareSearch, FindCareResults, RequiresPlan, and RedirectToPlanPicker below.
// config.PLAN_PICKER_URL is intentionally falsy for the dev tier (and, by extension,
// any local `npm run dev` session — see appConfig.js's autoDetectConfig()): dev/local
// sit below qa in the promotion pipeline and have no dedicated branded plan-picker
// page of their own, so — instead of defaulting to some other tier's URL, which used
// to silently bounce dev/local visitors up to the real qa.aetnabetterhealth.com — we
// simply don't navigate anywhere in that case and let the caller render an in-app
// fallback instead.
function redirectToPlanPicker() {
  if (!config.PLAN_PICKER_URL) {
    console.warn('[App] No PLAN_PICKER_URL configured for this environment (dev/local) — staying put instead of redirecting.');
    return false;
  }
  window.location.replace(config.PLAN_PICKER_URL);
  return true;
}

// Inline, dependency-free fallback shown in place of the plan-picker redirect on
// dev/local, where there is nowhere to send the user. Kept intentionally simple (no
// new stylesheet) since it only ever appears in non-production tiers.
function NoPlanPickerFallback() {
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>No plan code in URL</h2>
      <p>
        This environment doesn't redirect to a plan picker (dev/local only). Try a URL
        like <code>/find-care/FL</code> instead.
      </p>
    </div>
  );
}

// Top-level error boundary — prevents any uncaught render error from white-screening the app
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('❌ [ErrorBoundary] Uncaught render error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Something went wrong. Please refresh the page.</h2>
          {import.meta.env.DEV && (
            <pre style={{ textAlign: 'left', fontSize: '0.8rem', color: 'red' }}>
              {this.state.error?.toString()}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

// Component to handle /find-care/:planCode routing - renders search page
function FindCareSearch() {
  const { planCode: rawPlanCode } = useParams();
  // On localhost, default to PA per request — lets local dev testing hit the app
  // directly at e.g. "/" or "/find-care" without having to know/type a real plan
  // code, instead of always bouncing to the (nonexistent, in dev) plan picker.
  const planCode = rawPlanCode || (isLocalDevMode() ? 'PA' : rawPlanCode);
  const [tranKey, setTranKey] = useState(window.APP_CONFIG?.VITE_APP_TRANS_KEY);
  
  // If no plan in URL path, redirect to plan picker
  if (!planCode) {
    return redirectToPlanPicker() ? null : <NoPlanPickerFallback />;
  }
  
  // NOTE (Phase 0 — URL Parameter Removal): previously this mirrored `planCode` into a
  // `?plan=` query param via setSearchParams(). That caused the query string to be
  // re-populated on every render, which fights against any later "clean URL" logic in
  // Results/Search. The env plan code now flows straight from the path via the
  // `envPlanCode` prop below — it never touches the query string.

  useEffect(() => {
    try{
    console.log(tranKey)
    if(!tranKey){
      setTranKey(window.APP_CONFIG?.VITE_APP_TRANS_KEY)
    } 
    else{     
      // Ensure Akorbi stubs are always defined so failures never crash the app
    let akorbiEl = document.createElement('script');
    let akorbiStubsEl = document.createElement('script');
    akorbiEl.src= "https://app.akorbi-link.com/client/" + tranKey + "/0/stub.js";
    akorbiEl.async;
    akorbiEl.onerror = "window.__akorbiLoadFailed=true";
    akorbiStubsEl.textContent =   "window.crestStub = window.crestStub || { setCurrentLanguage: function(){}, start: function(){} };  window.crestTranslator = window.crestTranslator || { start: function(){ return false; } };"
    akorbiStubsEl.async;
    document.head.append(akorbiEl);
    document.head.append(akorbiStubsEl);
    }
  }
  catch(e){
    throw(e);
  }
  }, [tranKey])
  
  // Render Search without RequiresPlan wrapper since we have plan in path
  return <Search envPlanCode={planCode} />;
}

// Component to handle /find-care/:planCode/results routing
function FindCareResults() {
  const { planCode: rawPlanCode } = useParams();
  // On localhost, default to PA per request — see FindCareSearch above.
  const planCode = rawPlanCode || (isLocalDevMode() ? 'PA' : rawPlanCode);
  
  // If no plan in URL path, redirect to plan picker
  if (!planCode) {
    return redirectToPlanPicker() ? null : <NoPlanPickerFallback />;
  }
  
  // NOTE (Phase 0 — URL Parameter Removal): previously this mirrored `planCode` into a
  // `?plan=` query param via setSearchParams(). Removed — see FindCareSearch above for
  // the full rationale. The env plan code now flows straight from the path via the
  // `envPlanCode` prop below.

  // Render Results without RequiresPlan wrapper since we have plan in path
  return <Results envPlanCode={planCode} />;
}

// Guard: redirects to plan picker if no ?plan= param is present
function RequiresPlan({ children }) {
  const [searchParams] = useSearchParams();
  // On localhost, default straight to PA instead of showing the "No plan code in URL"
  // fallback — same local-dev convenience RedirectToPlanPicker/FindCareSearch/
  // FindCareResults already apply for the /, /find-care, and /find-care/:planCode
  // routes (see those functions' comments). /search and /results had been missed:
  // hitting either bare (e.g. http://localhost:3000/mcsui/search with no `?plan=`)
  // fell through to NoPlanPickerFallback instead, so NOTHING loaded locally — not
  // even the local dummy-data fallback — unless the tester remembered to manually
  // append `?plan=FL` (or similar) to the URL every time.
  if (!searchParams.get('plan') && isLocalDevMode()) {
    searchParams.set('plan', 'PA');
    return <Navigate to={`?${searchParams.toString()}`} replace />;
  }
  if (!searchParams.get('plan')) {
    return redirectToPlanPicker() ? null : <NoPlanPickerFallback />;
  }
  return children;
}

// Component to redirect to plan picker (handles external URLs)
function RedirectToPlanPicker() {
  const [noPicker, setNoPicker] = useState(false);
  const localDev = isLocalDevMode();
  useEffect(() => {
    // On localhost, skip the plan picker entirely — there's no local plan-picker
    // page to redirect to anyway (see redirectToPlanPicker's PLAN_PICKER_URL
    // comment above), and the <Navigate> below handles sending the user to PA.
    if (localDev) return;
    if (!redirectToPlanPicker()) setNoPicker(true);
  }, [localDev]);

  // On localhost, default straight to the PA search page per request — instead of
  // showing the "No plan code in URL" fallback that this route would otherwise
  // render (since dev/local has no configured PLAN_PICKER_URL to redirect to).
  if (localDev) {
    return <Navigate to="/find-care/PA" replace />;
  }
  return noPicker ? <NoPlanPickerFallback /> : null;
}

// Detect the correct basename at runtime so the app works whether served under
// /mcsui/ (original) or directly at the root of a domain (e.g. /find-care/IL).
function getBasename() {
  return window.location.pathname.startsWith('/mcsui') ? '/mcsui' : '';
}

// ---------------------------------------------------------------------------
// Client-side "ugly host -> pretty branded domain" redirect for the modern
// path-based /find-care/:planCode[/results] URLs (e.g.
// https://qaextaz-oci.aetna.com/mcsui/find-care/IL not redirecting to
// qa.aetnabetterhealth.com/find-care/IL, reported for IL — and, per this
// ticket, applies identically to every plan code EXCEPT AZ). server.js
// deliberately does NOT redirect this path-based shape (see its
// getUglyHostRedirectTarget's long comment): Akamai reverse-proxies the pretty
// branded domains (qa.aetnabetterhealth.com, qa.mercycareaz.org, etc.) straight
// to this same origin, rewriting the Host header to the ugly hostname to do
// so — so from the SERVER's point of view, a legitimate pretty-domain visitor
// and someone hitting the ugly host directly are indistinguishable (both
// arrive with e.g. Host: qaextaz-oci.aetna.com), and redirecting there would
// create an infinite self-redirect loop for the pretty-domain visitor.
//
// The BROWSER doesn't have this problem: window.location.hostname always
// reflects whatever hostname the user's browser actually navigated to (Akamai
// only rewrites the Host header on the proxied request to our origin server;
// it doesn't change what's in the address bar). So a user genuinely ON the
// ugly host directly has window.location.hostname === 'qaextaz-oci.aetna.com',
// while a user on the pretty domain (proxied through Akamai) has
// window.location.hostname === 'qa.aetnabetterhealth.com' the whole time —
// perfectly distinguishable here, even though the server can't tell them
// apart. That's what lets this redirect live safely in the React app instead.
//
// Mirrors server.js's UGLY_HOSTS/PRETTY_DOMAINS/MERCYCARE_PLAN_CODES
// constants — kept in sync manually since server.js runs under plain Node
// (no bundler) and can't import this client module (or vice versa).
const UGLY_HOSTS = ['qaextaz-oci.aetna.com', 'extaz-oci.aetna.com'];
const PRETTY_DOMAINS = {
  qaextaz: { aetna: 'qa.aetnabetterhealth.com', mercycare: 'qa.mercycareaz.org' },
  extaz: { aetna: 'aetnabetterhealth.com', mercycare: 'mercycareaz.org' }
};
// AZ is the only plan code mapped to the MercyCare brand — every other state
// (IL included) goes to the Aetna Better Health branded domain.
const MERCYCARE_PLAN_CODES = ['AZ'];
const FIND_CARE_PATH_PATTERN = /^\/(?:mcsui\/)?find-care\/([^/?#]+)/i;

// Returns the pretty-domain URL the browser should be redirected to, or null
// if no redirect is needed (wrong host, or not a /find-care/:planCode URL).
function getClientSideUglyHostRedirectTarget() {
  const hostname = window.location.hostname.toLowerCase();
  if (!UGLY_HOSTS.includes(hostname)) return null;

  const match = window.location.pathname.match(FIND_CARE_PATH_PATTERN);
  if (!match) return null;

  const planCode = match[1].toUpperCase();
  const domains = hostname === 'extaz-oci.aetna.com' ? PRETTY_DOMAINS.extaz : PRETTY_DOMAINS.qaextaz;
  const brandDomain = MERCYCARE_PLAN_CODES.includes(planCode) ? domains.mercycare : domains.aetna;

  // Strip a leading /mcsui prefix before handing the path to the pretty domain. The
  // ugly OpenShift host serves this app under /mcsui/find-care/:planCode, but the
  // pretty branded domains serve it at the bare root — /find-care/:planCode, with NO
  // /mcsui segment — which is also the only path pattern actually covered by the
  // Google Maps API key's HTTP-referrer allowlist for these domains. Copying
  // window.location.pathname verbatim (the previous behavior) produced
  // https://qa.aetnabetterhealth.com/mcsui/find-care/NY — a referer Google's
  // allowlist doesn't recognize, causing every Maps/Places call on that page to fail
  // with PERMISSION_DENIED even though the plain https://qa.aetnabetterhealth.com/
  // find-care/NY pattern (no /mcsui) works fine and was already confirmed allowlisted.
  const targetPath = window.location.pathname.replace(/^\/mcsui(?=\/|$)/i, '') || '/';

  // Preserve the full path (including /results if present) plus any query
  // string/hash, so this is a transparent hostname swap, not a navigation to
  // a different page within the app.
  return `https://${brandDomain}${targetPath}${window.location.search}${window.location.hash}`;
}

function App() {
  // Redirect the ugly host straight to the pretty branded domain for
  // /find-care/:planCode[/results] URLs — see getClientSideUglyHostRedirectTarget's
  // long comment above for why this must happen client-side rather than in
  // server.js. Computed once via useState's lazy initializer (runs exactly once,
  // synchronously, before the first render) rather than in a useEffect, so the ugly
  // host's own content is never even flashed on screen while an effect spins up —
  // window.location.replace() fires immediately, and rendering nothing (null) below
  // in that case avoids any visible flash of the wrong-domain UI in the meantime.
  const [redirectTarget] = useState(() => getClientSideUglyHostRedirectTarget());

  useEffect(() => {
    if (redirectTarget) {
      window.location.replace(redirectTarget);
    }
  }, [redirectTarget]);

  // Pre-fetch APIC token on app load so it's cached before the first API call.
  // This hides the token fetch + preflight latency behind the initial page load.
  useEffect(() => {
    getToken()
      .then(() => console.log('✅ APIC token pre-fetched and cached'))
      .catch((err) => console.warn('⚠️ APIC token pre-fetch failed (will retry on demand):', err.message));
  }, []);

  // Inject the Adobe Launch script for the current environment. Previously this was
  // baked into index.html at build time via the %VITE_ADOBE_LAUNCH_SCRIPTS% Vite
  // plugin placeholder (see vite.config.js's html-env-inject plugin) — moved here to
  // match the same runtime-injection pattern already used for the Akorbi translation
  // script above (tranKey/FindCareSearch), so the URL is resolved from
  // import.meta.env.VITE_ADOBE_LAUNCH_SCRIPTS at app startup instead of being
  // hardcoded into the served HTML.
  useEffect(() => {
    const scriptUrl = config.VITE_ADOBE_LAUNCH_SCRIPTS;
    if (!scriptUrl) {
      console.warn('⚠️ VITE_ADOBE_LAUNCH_SCRIPTS not set for this environment — Adobe Launch script not injected');
      return;
    }
    // Avoid injecting twice (e.g. React StrictMode double-invoking effects in dev)
    if (document.querySelector(`script[src="${scriptUrl}"]`)) {
      return;
    }
    const adobeLaunchEl = document.createElement('script');
    adobeLaunchEl.src = scriptUrl;
    adobeLaunchEl.async = true;
    document.body.appendChild(adobeLaunchEl);
  }, []);

  // Log initial URL and query parameters for bot/wildcard detection
  useEffect(() => {
    // Log the full URL and all query params
    try {
      const url = window.location.href;
      const searchParams = new URLSearchParams(window.location.search);
      const allParams = {};
      for (const [key, value] of searchParams.entries()) {
        allParams[key] = value;
      }
      // Log for Splunk/monitoring pickup
      console.log('[UI INIT] Page loaded:', {
        url,
        queryParams: allParams
      });
    } catch (e) {
      console.warn('[UI INIT] Failed to log URL/query params:', e);
    }
  }, []);

  // REMOVED: Background location detection to prevent conflicts and API throttling
  // The search page now handles location detection directly
  // useEffect(() => {
  //   const detectLocationInBackground = async () => {
  //     try {
  //       const result = await locationService.detectLocationAndPlan();
  //     } catch (error) {
  //       // Silent catch
  //     }
  //   };
  //   // Run detection once on app start
  //   detectLocationInBackground();
  // }, []);

  // Render nothing while the ugly-host -> pretty-domain redirect above is in flight,
  // rather than briefly mounting the full app (and its routes/data fetches) on a
  // hostname we're about to navigate away from.
  if (redirectTarget) {
    return null;
  }

  return (
    <ErrorBoundary>
      <PlanFeaturesProvider>
        <LocationProvider>
          <TranslationProvider>
            <SearchStateProvider>
              <Router basename={getBasename()}>
                <div className="App">
                  <Header />
                  <Routes>
                    <Route path="/find-care/:planCode/results" element={<FindCareResults />} />
                    <Route path="/find-care/:planCode" element={<FindCareSearch />} />
                    {/* Bare "/find-care" (with or without a trailing slash, e.g.
                        .../mcsui/find-care/) — deliberately separate from the
                        ":planCode" route above rather than relying on its
                        `if (!planCode)` guard: React Router compiles a required
                        param like ":planCode" into a regex group that demands at
                        least one non-slash character, so a path with nothing (or
                        only a trailing slash) after "/find-care" doesn't match that
                        route AT ALL. With no catch-all route either, <Routes>
                        rendered nothing and FindCareSearch never even mounted, so
                        its redirect-to-plan-picker guard never ran. This route
                        exists purely to catch that no-segment case and send it
                        through the same redirect. */}
                    <Route path="/find-care" element={<RedirectToPlanPicker />} />
                    <Route path="/search" element={<RequiresPlan><Search /></RequiresPlan>} />
                    <Route path="/results" element={<RequiresPlan><Results /></RequiresPlan>} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    {/* Root route: redirect to plan picker for default state selection */}
                    <Route path="/" element={<RedirectToPlanPicker />} />
                  </Routes>
                </div>
                <Footer />
              </Router>
            </SearchStateProvider>
          </TranslationProvider>
        </LocationProvider>
      </PlanFeaturesProvider>
    </ErrorBoundary>
  );
}

export default App;
